import { ChangeDetectionStrategy, Component, ElementRef, HostListener, ViewChild, WritableSignal, computed, input, output, signal } from '@angular/core';
import {
  ICustomToggleOptions,
  IFilterPanelSelection,
  IFilterPanelValues,
  IScheduleViewOptions,
  IVehicleScheduleFilters,
} from 'src/app/models';
import {
  CustomToggleTone,
  FilterType,
  ScheduleGroupBy,
  ScheduleViewMode,
  ScheduleViewOption,
} from 'src/app/models/enums';
import { FILTER_PANEL_ADAPTERS } from 'src/app/shared/filter-panel/filterPanelAdapters';
import { visibleSpanLabel } from '../scheduleZoom';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { images } from 'src/images';
import { APP } from 'src/app/config/app';

@Component({
  selector: 'app-vehicle-search-bar',
  templateUrl: './vehicle-search-bar.component.html',
  styleUrls: ['./vehicle-search-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VehicleSearchBarComponent {
  @ViewChild('viewOptionsDropdown') viewOptionsDropdown?: ElementRef<HTMLDetailsElement>;
  @ViewChild('viewModeDropdown') viewModeDropdown?: ElementRef<HTMLDetailsElement>;

  readonly viewMode = input(ScheduleViewMode.DAY);
  readonly groupBy = input(ScheduleGroupBy.VEHICLES);
  readonly zoom = input(0);
  readonly visibleHours = input(APP.SCHEDULE_ALL_HOURS);
  readonly visibleDays = input(APP.SCHEDULE_ALL_WEEKDAYS);
  readonly filters = input<IVehicleScheduleFilters>();
  readonly viewOptions = input(APP.SCHEDULE_DEFAULT_VIEW_OPTIONS);

  readonly searchEventEmitter = output<string>();
  readonly changeViewMode = output<ScheduleViewMode>();
  readonly changeGroupBy = output<ScheduleGroupBy>();
  readonly changeZoom = output<number>();
  readonly goToNowEventEmitter = output<void>();
  readonly filterEventEmitter = output<IVehicleScheduleFilters>();
  readonly newVehicleEventEmitter = output<void>();
  readonly changeViewOptions = output<IScheduleViewOptions>();

  filterOpened = signal(false);
  viewOptionsOpened = signal(false);
  filterSelection = computed<IFilterPanelSelection | null>(() => {
    const filters = this.filters();
    return filters ? FILTER_PANEL_ADAPTERS[FilterType.VEHICLES]!.toSelection(filters) : null;
  });
  filtersApplied = computed(() => {
    const filters = this.filters();
    if (!filters) return 0;
    const { from, to, ...lists } = filters;
    return this.utilsService.getFiltersCount({ ...lists, dateRange: from || to });
  });

  public images = images;
  readonly groupings = ScheduleGroupBy;
  readonly groupByToggleOptions: ICustomToggleOptions<ScheduleGroupBy> = [
    {
      value: ScheduleGroupBy.VEHICLES,
      image: images.bus,
      descriptionKey: 'vehicles.vehicles',
      tone: CustomToggleTone.SECONDARY,
    },
    {
      value: ScheduleGroupBy.DRIVERS,
      image: images.driver,
      descriptionKey: 'vehicles.drivers',
      tone: CustomToggleTone.SECONDARY,
    },
  ];
  readonly viewOptionKeys = Object.values(ScheduleViewOption);
  readonly filterTypes = FilterType;
  readonly filterFields = APP.VEHICLES_FILTER_FIELDS;

  zoomLabel = computed(() =>
    visibleSpanLabel(this.viewMode(), this.zoom(), this.visibleHours().length * APP.MINUTES_PER_HOUR, this.visibleDays().length),
  );

  readonly viewModes = Object.values(ScheduleViewMode);
  readonly viewModeLabels = APP.SCHEDULE_VIEW_MODE_LABELS;
  viewModeOpened = signal(false);

  constructor(
    public literalService: LiteralService,
    private utilsService: UtilsService,
    private eRef: ElementRef,
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.filterOpened() && !this.eRef.nativeElement.contains(event.target)) {
      this.filterOpened.set(false);
    }
    [this.viewOptionsDropdown, this.viewModeDropdown]
      .filter((dropdown) => dropdown?.nativeElement.open && !dropdown.nativeElement.contains(event.target as Node))
      .forEach((dropdown) => this.closeDropdown(dropdown));
  }

  onDropdownToggle(opened: WritableSignal<boolean>, dropdown: HTMLDetailsElement) {
    opened.set(dropdown.open);
  }

  closeDropdown(dropdown?: ElementRef<HTMLDetailsElement>) {
    if (dropdown) {
      dropdown.nativeElement.open = false;
    }
  }

  onViewOptionChange(option: ScheduleViewOption, event: Event) {
    this.changeViewOptions.emit({ ...this.viewOptions(), [option]: (event.target as HTMLInputElement).checked });
  }

  onGroupByToggle() {
    this.changeGroupBy.emit(this.groupBy() === ScheduleGroupBy.VEHICLES ? ScheduleGroupBy.DRIVERS : ScheduleGroupBy.VEHICLES);
  }

  toggleFilter() {
    this.filterOpened.update((opened) => !opened);
  }

  onFilter(values: IFilterPanelValues) {
    this.filterEventEmitter.emit(FILTER_PANEL_ADAPTERS[FilterType.VEHICLES]!.toFilters(values));
  }

  onViewModeChange(value: ScheduleViewMode) {
    this.closeDropdown(this.viewModeDropdown);
    if (value !== this.viewMode()) {
      this.changeViewMode.emit(value);
    }
  }

  onZoomChange(event: any) {
    const value = typeof(event) === 'number' ? event : Number((event.target as HTMLInputElement).value);
    this.changeZoom.emit(value);
  }

  onNewVehicle() {
    this.newVehicleEventEmitter.emit();
  }
}
